(() => {
  let tr = (key, variables = {}) => key.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => String(variables[name] ?? ""));
  let runtimeLabels = {};

  function configureTranslations(section) {
    const node = section.querySelector("[data-pd-valkyrie-translations]");
    let dictionary = {};
    runtimeLabels = {};
    try {
      const payload = JSON.parse(node?.textContent || "{}");
      dictionary = payload && typeof payload === "object" && !Array.isArray(payload) && payload.js && typeof payload.js === "object" ? payload.js : {};
      runtimeLabels = payload && typeof payload.runtime === "object" && payload.runtime && !Array.isArray(payload.runtime) ? payload.runtime : {};
    } catch (error) { console.error(error); }
    tr = (key, variables = {}) => String(dictionary[key] || key).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => String(variables[name] ?? ""));
  }

  function runtimeLabel(family, id, nativeValue) {
    const familyLabels = runtimeLabels?.[family];
    const localized = familyLabels && typeof familyLabels === "object" ? familyLabels[String(id)] : "";
    return typeof localized === "string" && localized.trim() ? localized : String(nativeValue ?? "");
  }

  const SELECTORS = {
    section: "[data-pd-valkyrie-section]",
    tabs: "[data-pd-tabs]",
    stockGrid: "[data-stock-grid]",
    designer: "[data-pd-designer]",
    heroGallery: "[data-hero-gallery]"
  };

  const DEFAULT_COLOR = "#b2b486";
  const PREVIEW_MESH_OPACITY = 0.85;
  const DEFAULT_DESIGN_NAME = "My Valkyrie";
  const DEFAULT_VIEW_ZOOM = 72;
  const COMPACT_VIEW_ZOOM = 88;
  const COMPACT_VIEW_MAX_WIDTH = 420;
  const MIN_VIEW_ZOOM = 12;
  const MAX_VIEW_ZOOM = 110;
  const SHARE_PARAM = "pd_design";
  const RDS_SLIDER_DEFAULT_SIZE = "21 x 30";
  const DEFAULT_MODE_SET_ID = 1127;
  const RDS_SLIDER_CHOICES = {
    standard: { labelKey: "standard_slider", cartLabelKey: "standard_slider_cart", variantStyle: "" },
    rds: { labelKey: "rds_slider", cartLabelKey: "rds_slider_cart", variantStyle: "RDS" },
    "collapsible-rds": { labelKey: "collapsible_rds_slider", cartLabelKey: "collapsible_rds_slider_cart", variantStyle: "Collapsible RDS" }
  };
  const sliderChoice = (key) => ({ ...RDS_SLIDER_CHOICES[key], label: tr(RDS_SLIDER_CHOICES[key].labelKey), cartLabel: tr(RDS_SLIDER_CHOICES[key].cartLabelKey) });
  const EUROPEAN_COUNTRY_CODES = new Set([
    "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
    "IS", "LI", "NO", "CH", "GB", "UK", "AD", "MC", "SM", "VA"
  ]);

  function cleanHex(hex) {
    const raw = String(hex || "").replace("#", "").trim().toLowerCase();
    const expanded = /^[0-9a-f]{3}$/i.test(raw)
      ? raw.split("").map((char) => `${char}${char}`).join("")
      : raw;
    return `#${expanded}`;
  }
  const compactHex = (hex) => cleanHex(hex).replace("#", "");
  const getJson = async (url) => {
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) throw new Error(tr("could_not_load", { url }));
    return response.json();
  };

  const escapeHTML = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[char]);

  function liveStockRequestUrl(url, section) {
    if (!url) return "";
    try {
      const parsed = new URL(url, window.location.origin);
      const params = {
        country: section.dataset.customerCountry,
        currency: section.dataset.moneyCurrency,
        market: section.dataset.marketHandle,
        market_name: section.dataset.marketName
      };
      Object.entries(params).forEach(([key, value]) => {
        if (value) parsed.searchParams.set(key, value);
      });
      return parsed.toString();
    } catch {
      return url;
    }
  }

  function stockModelIdAllowList(section) {
    return new Set(String(section?.dataset?.stockModelIds || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean));
  }

  function stockItemAllowedForSection(section, item) {
    const allowedIds = stockModelIdAllowList(section);
    if (!allowedIds.size) return true;
    return allowedIds.has(String(item?.canopy_model_id || item?.canopyModelId || ""));
  }

  function stockItemsForSection(section, items = []) {
    return (Array.isArray(items) ? items : []).filter((item) => stockItemAllowedForSection(section, item));
  }

  function stockDataForSection(section, data) {
    if (!data) return data;
    const items = stockItemsForSection(section, data.items || []);
    return { ...data, items, count: items.length };
  }

  function encodeBase64Url(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (let index = 0; index < bytes.length; index += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }

  function decodeBase64Url(value) {
    const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`;
    const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new TextDecoder().decode(bytes);
  }

  function initTabs(root) {
    root.querySelectorAll(SELECTORS.tabs).forEach((tabs) => {
      const buttons = [...tabs.querySelectorAll("[data-tab]")];
      const panels = [...tabs.querySelectorAll("[data-panel]")];
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) => {
            item.classList.toggle("is-active", item === button);
            item.setAttribute("aria-selected", item === button ? "true" : "false");
          });
          panels.forEach((panel) => {
            panel.classList.toggle("is-active", panel.dataset.panel === button.dataset.tab);
          });
        });
      });
    });
  }

  function initHeroGallery(root) {
    root.querySelectorAll(SELECTORS.heroGallery).forEach((gallery) => {
      const slides = [...gallery.querySelectorAll("[data-gallery-slide]")];
      const thumbs = [...gallery.querySelectorAll("[data-gallery-thumb]")];
      const previous = gallery.querySelector("[data-gallery-prev]");
      const next = gallery.querySelector("[data-gallery-next]");
      if (slides.length <= 1) return;
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));

      function setActive(index) {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle("is-active", slideIndex === activeIndex));
        thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === activeIndex;
          thumb.classList.toggle("is-active", isActive);
          thumb.setAttribute("aria-current", isActive ? "true" : "false");
        });
      }

      thumbs.forEach((thumb, index) => thumb.addEventListener("click", () => setActive(index)));
      previous?.addEventListener("click", () => setActive(activeIndex - 1));
      next?.addEventListener("click", () => setActive(activeIndex + 1));
      setActive(activeIndex);
    });
  }

  function initStock(section, stockData) {
    const grid = section.querySelector(SELECTORS.stockGrid);
    const template = section.querySelector("[data-stock-card-template]");
    const filterButtons = [...section.querySelectorAll("[data-stock-filter]")];
    const sizeOptions = section.querySelector("[data-stock-size-options]");
    const count = section.querySelector("[data-stock-count]");
    const featured = section.querySelector("[data-featured-stock]");
    const fallbackVariantId = section.dataset.stockVariantId;
    const stockPrice = section.dataset.stockPrice;
    const stockPriceCents = Number(section.dataset.stockPriceCents || 0);
    const moneyCurrency = section.dataset.moneyCurrency || "EUR";
    const moneyLocale = String(section.dataset.moneyLocale || document.documentElement.lang || "en").replace("_", "-");
    const customerCountry = String(section.dataset.customerCountry || window.Shopify?.country || "").toUpperCase();
    const marketHandle = String(section.dataset.marketHandle || "").toLowerCase();
    const marketName = String(section.dataset.marketName || "").toLowerCase();
    const canopyVariants = parseVariantJson(section.querySelector("[data-canopy-variants-json]"));
    const clearanceVariants = parseVariantJson(section.querySelector("[data-clearance-variants-json]"));
    const rdsSliderVariants = parseVariantJson(section.querySelector("[data-rds-slider-variants-json]"));
    const sliderOptions = {
      standard: { label: tr("standard_slider"), cartLabel: tr("standard_slider_cart"), variant: null, priceCents: 0 },
      rds: { label: tr("rds_slider"), cartLabel: tr("rds_slider_cart"), variant: sliderVariantFor("RDS"), priceCents: Number(section.dataset.rdsSliderPriceCents || 56000) },
      "collapsible-rds": { label: tr("collapsible_rds_slider"), cartLabel: tr("collapsible_rds_slider_cart"), variant: sliderVariantFor("Collapsible RDS"), priceCents: Number(section.dataset.collapsibleRdsSliderPriceCents || 59600) }
    };
    const enableCartPosts = section.dataset.enableCartPosts === "true";
    const stockValidationUrl = section.dataset.stockValidationUrl;
    let region = "all";
    let size = "all";

    if (!grid || !template || !stockData?.items) return;

    function parseVariantJson(node) {
      if (!node?.textContent?.trim()) return [];
      try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error("Could not parse Valkyrie variant JSON.", error);
        return [];
      }
    }

    function normalizeOption(value) {
      return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "").trim();
    }

    function variantHasOption(variant, expected) {
      const target = normalizeOption(expected);
      return [...(variant.options || []), variant.title].some((value) => normalizeOption(value).includes(target));
    }

    function variantMatchesSliderStyle(variant, style) {
      const normalizedStyle = normalizeOption(style);
      const values = [...(variant.options || []), variant.title];
      return values.some((value) => {
        const normalized = normalizeOption(value);
        if (normalizedStyle === "rds") {
          return normalized === "rds" || (normalized.includes("rds") && !normalized.includes("collapsible"));
        }
        return normalized.includes(normalizedStyle);
      });
    }

    function sliderVariantFor(style) {
      const styleMatches = (variant) => variant.available !== false && variantMatchesSliderStyle(variant, style);
      return rdsSliderVariants.find((variant) => styleMatches(variant) && variantHasOption(variant, RDS_SLIDER_DEFAULT_SIZE))
        || rdsSliderVariants.find(styleMatches)
        || null;
    }

    function canopyVariantForSize(sizeValue) {
      return canopyVariants.find((variant) => variant.available !== false && variantHasOption(variant, sizeValue) && variantHasOption(variant, "Hybrid")) || null;
    }

    function clearanceVariantForSize(sizeValue) {
      return clearanceVariants.find((variant) => variant.available !== false && variantHasOption(variant, sizeValue)) || null;
    }

    function variantPriceCents(variant, fallback = 0) {
      const cents = Number(variant?.price || 0);
      return Number.isFinite(cents) && cents > 0 ? cents : fallback;
    }

    function variantPriceText(variant, fallback = "") {
      const cents = variantPriceCents(variant);
      return cents > 0 ? money(cents, fallback) : (variant?.price_formatted || fallback);
    }

    function categoryFor(item) {
      return String(item.category || item.details?.category || item.details?.pattern || "").trim();
    }

    function isClearance(item) {
      return /clearance/i.test(categoryFor(item));
    }

    function isUsedOrResale(item) {
      return /\b(used|resale)\b/i.test(categoryFor(item));
    }

    function isEuropeanMarket() {
      const marketLooksEuropean = /\b(eu|europe|european)\b/i.test(`${marketHandle} ${marketName}`);
      const countryLooksEuropean = customerCountry ? EUROPEAN_COUNTRY_CODES.has(customerCountry) : false;
      return moneyCurrency === "EUR" && (marketLooksEuropean || countryLooksEuropean || !customerCountry);
    }

    function sourceUsdCentsFor(item) {
      const explicit = Number(item.source_price_cents || 0);
      if (Number.isFinite(explicit) && explicit > 0) return explicit;
      const priceText = String(item.price_display || "");
      const match = priceText.match(/\$\s*([0-9][0-9,]*(?:\.\d{1,2})?)/);
      if (!match) return 0;
      return Math.round(Number(match[1].replace(/,/g, "")) * 100);
    }

    function formatUsd(cents) {
      if (!Number.isFinite(cents) || cents <= 0) return "";
      try {
        return new Intl.NumberFormat(moneyLocale, { style: "currency", currency: "USD" }).format(cents / 100);
      } catch {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    const stockItems = stockItemsForSection(section, stockData.items);

    function money(cents, fallback = "") {
      if (!Number.isFinite(cents) || cents <= 0) return fallback;
      try {
        return new Intl.NumberFormat(moneyLocale, { style: "currency", currency: moneyCurrency }).format(cents / 100);
      } catch {
        return fallback || `€${(cents / 100).toFixed(2)}`;
      }
    }

    function usedPriceTextFor(item) {
      if (item.price_currency && item.price_display) return item.price_display;
      if (isEuropeanMarket()) {
        if (item.used_price_eur_display) return item.used_price_eur_display;
        const eurCents = Number(item.used_price_eur_cents || 0);
        if (Number.isFinite(eurCents) && eurCents > 0) return money(eurCents);
      }
      return item.source_price_display || item.price_display || formatUsd(sourceUsdCentsFor(item));
    }

    function usedPriceLabelFor() {
      return isEuropeanMarket() ? tr("used_price") : tr("source_price");
    }

    const sizes = [...new Set(stockItems.map((item) => item.size).filter(Boolean))]
      .sort((a, b) => Number(a) - Number(b));
    sizes.forEach((stockSize) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.stockSize = stockSize;
      button.textContent = stockSize;
      sizeOptions?.append(button);
    });
    const sizeButtons = () => [...section.querySelectorAll("[data-stock-size]")];

    function filteredItems() {
      return stockItems.filter((item) => {
        const matchesRegion = region === "all" || item.locations.includes(region);
        const matchesSize = size === "all" || item.size === size;
        return matchesRegion && matchesSize;
      });
    }

    function detailsFor(item) {
      if (isUsedOrResale(item)) {
        return [
          [tr("condition_notes"), item.details?.condition_notes]
        ].filter(([, value]) => value);
      }
      return [];
    }

    function flagFor(location) {
      const value = String(location || "").toUpperCase();
      if (value === "US") return "🇺🇸";
      if (value === "EU") return "🇪🇺";
      return "🌐";
    }

    function stockReferenceFor(item) {
      return item.details?.name || String(item.stock_id);
    }

    function stockDisplayName(item) {
      return runtimeLabel("stock_names", item?.stock_id, item?.name || "");
    }

    function stockCountText(total) {
      return tr("stock_count", { total, item: total === 1 ? tr("canopy_singular") : tr("canopy_plural") });
    }

    function renderFeatured() {
      const item = stockItems[0];
      if (!featured || !item) return;
      featured.innerHTML = `
        <img src="${escapeHTML(item.snapshot_url)}" alt="${escapeHTML(tr("stock_canopy_alt", { name: stockDisplayName(item) }))}">
        <div>
          <strong>${escapeHTML(stockDisplayName(item))}</strong>
          <p>${escapeHTML(item.locations.join(" + "))} stock &bull; ${escapeHTML(item.details?.line_set || tr("line_set_listed"))}</p>
        </div>
      `;
    }

    async function validateStockItem(item, location) {
      if (!stockValidationUrl) {
        return { available: true, unverified: true };
      }
      const response = await fetch(stockValidationUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          stock_id: item.stock_id,
          stock_hash: item.stock_hash,
          model: item.model,
          size: item.size,
          location
        })
      });
      if (!response.ok) return { available: true, unverified: true, validation_status: `HTTP ${response.status}` };
      const result = await response.json();
      if (result.available === false) throw new Error(tr("stock_unavailable"));
      return result;
    }

    function stockVariantFor(item) {
      if (isUsedOrResale(item)) return null;
      return isClearance(item) ? clearanceVariantForSize(item.size) : canopyVariantForSize(item.size);
    }

    function stockPriceTextFor(item) {
      return isUsedOrResale(item) ? usedPriceTextFor(item) : variantPriceText(stockVariantFor(item), stockPrice);
    }

    function selectedSliderFor(card) {
      const value = card.querySelector("[data-stock-slider] select")?.value || "standard";
      const slider = sliderOptions[value] || sliderOptions.standard;
      return { ...slider, key: value };
    }

    function updateCardPrice(card, item) {
      const price = card.querySelector("[data-stock-card-price]");
      if (!price) return;
      if (isUsedOrResale(item)) {
        const usedPriceText = usedPriceTextFor(item);
        price.hidden = !usedPriceText;
        price.querySelector("span").textContent = usedPriceLabelFor();
        price.querySelector("strong").textContent = usedPriceText;
        return;
      }
      const selectedSlider = selectedSliderFor(card);
      const baseVariant = stockVariantFor(item);
      const baseCents = variantPriceCents(baseVariant, isClearance(item) ? Math.max(0, stockPriceCents - 20000) : stockPriceCents);
      const baseText = stockPriceTextFor(item);
      price.hidden = !baseText;
      price.querySelector("span").textContent = isClearance(item) ? tr("clearance_price") : tr("stock_price");
      price.querySelector("strong").textContent = selectedSlider.priceCents
        ? money(baseCents + selectedSlider.priceCents, baseText)
        : baseText;
    }

    async function addStockToCart(item, location, slider) {
      if (isUsedOrResale(item)) {
        throw new Error(tr("used_resale_requires_manual"));
      }
      const canopyVariant = stockVariantFor(item);
      const canopyVariantId = canopyVariant?.id || fallbackVariantId;
      if (!canopyVariantId || canopyVariantId === "undefined") {
        throw new Error(tr("no_variant_found", { type: isClearance(item) ? tr("clearance") : tr("stock_price"), size: item.size }));
      }
      if (slider.priceCents && !slider.variant?.id) {
        throw new Error(tr("slider_variant_missing", { slider: slider.label, size: RDS_SLIDER_DEFAULT_SIZE }));
      }
      const validation = await validateStockItem(item, location);
      if (!enableCartPosts) {
        return { previewOnly: true, validation };
      }
      const stockReference = stockReferenceFor(item);
      const sliderLabel = slider.cartLabel || slider.label;
      const canopyProperties = {
        "Stock reference": stockReference,
        "PD stock ID": String(item.stock_id),
        "PD design URL": item.design_object_url || "",
        "Ships from": location,
        "_pd_order_type": "PD stock canopy",
        "_pd_stock_ref": String(item.stock_id),
        "_pd_stock_hash": item.stock_hash || "",
        "_pd_stock_reference": stockReference,
        "_pd_model": item.model,
        "_pd_size": item.size,
        "_pd_stock_location": location,
        "_pd_stock_category": categoryFor(item),
        "_pd_stock_condition": isClearance(item) ? "CLEARANCE" : "Standard Stock",
        "_pd_slider": sliderLabel,
        "_pd_slider_surcharge": slider.priceCents ? money(slider.priceCents, `€${(slider.priceCents / 100).toFixed(0)}`) : "Included",
        "_pd_line_set": item.details?.line_set || "",
        "_pd_stock_view_url": item.view_url || "",
        "_pd_stock_design_object_url": item.design_object_url || "",
        "_pd_stock_clearance": isClearance(item) ? "true" : "false",
        "_pd_stock_validated": validation.unverified ? "not configured" : "true"
      };
      if (isClearance(item)) {
        canopyProperties["Stock condition"] = "CLEARANCE";
      }
      const items = [{ id: Number(canopyVariantId), quantity: 1, properties: canopyProperties }];
      if (slider.priceCents) {
        items.push({
          id: Number(slider.variant.id),
          quantity: 1,
          properties: {
            "For canopy": stockReference,
            "_pd_order_type": "PD stock canopy slider add-on",
            "_pd_stock_ref": String(item.stock_id),
            "_pd_stock_reference": stockReference,
            "_pd_slider": sliderLabel,
            "_pd_default_slider_size": RDS_SLIDER_DEFAULT_SIZE
          }
        });
      }
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items
        })
      });
      if (!response.ok) throw new Error(tr("stock_cart_rejected"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      return response.json();
    }

    function render() {
      const items = filteredItems();
      grid.innerHTML = "";
      if (count) {
        count.textContent = stockCountText(items.length);
      }
      if (!items.length) {
        grid.innerHTML = `<p class="pd-empty">${escapeHTML(tr("stock_empty"))}</p>`;
        return;
      }
      items.forEach((item) => {
        const node = template.content.firstElementChild.cloneNode(true);
        node.querySelector("img").src = item.snapshot_url;
        node.querySelector("img").alt = tr("stock_canopy_alt", { name: stockDisplayName(item) });
        node.querySelector("h3").textContent = stockDisplayName(item);
        const badges = node.querySelector(".pd-badges");
        item.locations.forEach((location) => {
          const badge = document.createElement("span");
          badge.textContent = `${flagFor(location)} ${location}`;
          badges.append(badge);
        });
        if (isClearance(item)) {
          const badge = document.createElement("span");
          badge.className = "pd-badge--clearance";
          badge.textContent = tr("clearance");
          badges.append(badge);
        }
        if (isUsedOrResale(item)) {
          const badge = document.createElement("span");
          badge.className = "pd-badge--condition";
          badge.textContent = tr("used_resale");
          badges.append(badge);
        }
        const price = node.querySelector("[data-stock-card-price]");
        if (price) updateCardPrice(node, item);
        const stockSlider = node.querySelector("[data-stock-slider]");
        if (isUsedOrResale(item)) {
          stockSlider?.remove();
        } else {
          stockSlider?.addEventListener("change", () => updateCardPrice(node, item));
        }
        const dl = node.querySelector("dl");
        detailsFor(item).forEach(([label, value]) => {
          const row = document.createElement("div");
          row.className = `pd-stock-card__detail pd-stock-card__detail--${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
          row.innerHTML = `<dt>${escapeHTML(label)}</dt><dd>${escapeHTML(value)}</dd>`;
          dl.append(row);
        });
        const stockButton = node.querySelector("[data-stock-add]");
        const reserveWhatsAppNumber = "34644271546";
        const reserveWhatsAppUrl = () => {
          const stockRef = item.stock_id ? ` (stock ID ${item.stock_id})` : "";
          const pageUrl = window.location.href.split("#")[0];
          return `https://wa.me/${reserveWhatsAppNumber}?text=${encodeURIComponent(`Hi, I want to reserve this PD Valkyrie stock canopy: ${stockDisplayName(item)}${stockRef}. ${pageUrl}`)}`;
        };
        if (isUsedOrResale(item)) {
          stockButton.textContent = tr("contact_reserve");
          stockButton.classList.add("pd-button--manual");
          const note = document.createElement("p");
          note.className = "pd-stock-card__manual-note";
          note.textContent = tr("manual_reservation_note");
          stockButton.insertAdjacentElement("beforebegin", note);
          stockButton.addEventListener("click", () => {
            window.location.href = reserveWhatsAppUrl();
          });
        } else {
          stockButton.addEventListener("click", async (event) => {
            const button = event.currentTarget;
            const selectedLocation = item.locations.includes(region) && region !== "all" ? region : item.locations[0];
            button.disabled = true;
            button.textContent = tr("adding");
            try {
              const result = await addStockToCart(item, selectedLocation, selectedSliderFor(node));
              button.textContent = result.previewOnly ? tr("cart_preview_ready") : tr("added_to_cart");
            } catch (error) {
              button.textContent = tr("check_settings");
              console.error(error);
            } finally {
              setTimeout(() => {
                button.disabled = false;
                button.textContent = tr("add_to_cart");
              }, 2400);
            }
          });
        }
        grid.append(node);
      });
    }

    filterButtons.forEach((button) => {
      button.addEventListener("click", () => {
        region = button.dataset.stockFilter;
        filterButtons.forEach((item) => item.classList.toggle("is-active", item === button));
        render();
      });
    });
    sizeOptions?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-stock-size]");
      if (!button) return;
      size = button.dataset.stockSize;
      sizeButtons().forEach((item) => item.classList.toggle("is-active", item === button));
      render();
    });

    renderFeatured();
    render();
  }

  function initDesigner(section, templateData, colorData) {
    const designer = section.querySelector(SELECTORS.designer);
    if (!designer || !templateData?.model) return;

    const model = templateData.model;
    const partsById = new Map(model.parts.map((part) => [part.id, part]));
    const canvas = designer.querySelector("[data-designer-canvas]");
    const loading = designer.querySelector("[data-designer-loading]");
    const swatches = designer.querySelector("[data-color-swatches]");
    const modes = designer.querySelector("[data-color-modes]");
    const status = designer.querySelector("[data-designer-status]");
    const currentColor = designer.querySelector("[data-current-color]");
    const sizeSelect = designer.querySelector("[data-design-size]");
    const sliderSelect = designer.querySelector("[data-design-slider]");
    const rdsSliderVariantsJson = designer.querySelector("[data-rds-slider-variants-json]");
    const snapshotInput = designer.querySelector("[data-design-snapshot]");
    const copyLinkButton = designer.querySelector("[data-designer-copy-link]");
    const whatsAppButton = designer.querySelector("[data-designer-whatsapp]");
    const shareButton = designer.querySelector("[data-designer-share]");
    const shareActions = designer.querySelector("[data-share-actions]");
    const variantId = section.dataset.customVariantId;
    const enableCartPosts = section.dataset.enableCartPosts === "true";
    const designSaveUrl = section.dataset.designSaveUrl;
    const assetBase = section.dataset.assetProbe.replace(/pd-valkyrie-designer(?:-v\d+)?(?:\.localized)?\.js(?:\?.*)?$/, "");
    const defaultModeSet = model.colorPartGroupSets.find((set) => Number(set.id) === DEFAULT_MODE_SET_ID);
    const rdsSliderVariants = parseRdsSliderVariants();
    const canopyVariants = parseCanopyVariants();

    const state = {
      selectedColor: DEFAULT_COLOR,
      selectedMode: defaultModeSet ? { type: "set", setId: defaultModeSet.id } : { type: "individual" },
      expanded: false,
      yaw: -0.8,
      pitch: -0.1,
      zoom: DEFAULT_VIEW_ZOOM,
      defaultZoom: DEFAULT_VIEW_ZOOM,
      partColors: new Map(model.parts.map((part) => [part.id, part.color || DEFAULT_COLOR])),
      meshes: new Map(),
      ready: false,
      pointer: null,
      coloredPartIds: new Set(model.parts.filter((part) => !part.hasFixedColor && part.color).map((part) => part.id)),
      loadedFromShare: false,
      designName: DEFAULT_DESIGN_NAME,
      currentShare: null
    };

    let renderer;
    let scene;
    let camera;
    let rootGroup;
    let raycaster;
    let pointerVector;

    function parseRdsSliderVariants() {
      if (!rdsSliderVariantsJson?.textContent?.trim()) return [];
      try {
        const variants = JSON.parse(rdsSliderVariantsJson.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error("Could not parse PD RDS slider variants.", error);
        return [];
      }
    }

    function parseCanopyVariants() {
      const node = section.querySelector("[data-canopy-variants-json]");
      if (!node?.textContent?.trim()) return [];
      try {
        const variants = JSON.parse(node.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error("Could not parse PD Valkyrie variants.", error);
        return [];
      }
    }

    function normalizeOption(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "")
        .trim();
    }

    function variantMatchesSliderStyle(variant, style) {
      const normalizedStyle = normalizeOption(style);
      const values = [...(variant.options || []), variant.title];
      return values.some((value) => {
        const normalized = normalizeOption(value);
        if (normalizedStyle === "rds") {
          return normalized === "rds" || (normalized.includes("rds") && !normalized.includes("collapsible"));
        }
        return normalized.includes(normalizedStyle);
      });
    }

    function variantMatchesSliderSize(variant, size) {
      const normalizedSize = normalizeOption(size);
      return [...(variant.options || []), variant.title].some((value) => normalizeOption(value).includes(normalizedSize));
    }

    function selectedSliderChoice() {
      const key = sliderSelect?.value || "standard";
      return sliderChoice(key in RDS_SLIDER_CHOICES ? key : "standard");
    }

    function selectedSliderVariant() {
      const choice = selectedSliderChoice();
      if (!choice.variantStyle) return null;
      return rdsSliderVariants.find((variant) => variant.available !== false && variantMatchesSliderStyle(variant, choice.variantStyle) && variantMatchesSliderSize(variant, RDS_SLIDER_DEFAULT_SIZE));
    }

    function selectedCanopyVariant() {
      const selectedSize = sizeSelect?.value || "";
      return canopyVariants.find((variant) => variant.available !== false && variantMatchesSliderSize(variant, selectedSize) && variantMatchesSliderStyle(variant, "Hybrid")) || null;
    }

    function refreshCartDrawer() {
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
    }

    function setStatus(message, tone = "info") {
      if (!status) return;
      const visibleMessage = tone === "error" || tone === "success" ? (message || "") : "";
      status.textContent = visibleMessage;
      status.hidden = !visibleMessage;
      status.classList.toggle("is-error", tone === "error");
      status.classList.toggle("is-success", tone === "success");
    }

    const SHORTENER_ENDPOINT = "https://tfx-photo-upload.young-sky-a2c1.workers.dev";

    function whatsappShareUrl(url) {
      return `https://wa.me/?text=${encodeURIComponent(tr("share_whatsapp", { url }))}`;
    }

    async function shortenShareUrl(url, prefix = "pd-valkyrie") {
      try {
        const response = await fetch(`${SHORTENER_ENDPOINT}/shorten`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url, prefix })
        });
        if (!response.ok) return url;
        const result = await response.json();
        return result.shortURL || result.shortUrl || result.url || url;
      } catch {
        return url;
      }
    }

    function setShareLink(url) {
      if (shareActions) shareActions.hidden = !url;
      if (shareButton && !shareButton.disabled) shareButton.textContent = tr("save_design");
      if (copyLinkButton) {
        copyLinkButton.disabled = !url;
        if (!url) {
          copyLinkButton.textContent = tr("copy_design_link");
          copyLinkButton.classList.remove("pd-share-action--success");
        }
      }
      if (whatsAppButton) {
        whatsAppButton.setAttribute("aria-disabled", url ? "false" : "true");
        if (url) {
          whatsAppButton.setAttribute("href", whatsappShareUrl(url));
        } else {
          whatsAppButton.removeAttribute("href");
        }
      }
    }

    function clearShareLink() {
      state.currentShare = null;
      setShareLink("");
    }

    function colorName(hex) {
      const key = compactHex(hex);
      const nativeName = colorData?.colors?.[key] || cleanHex(hex);
      return runtimeLabel("colors", key, nativeName);
    }

    function meshUrl(part) {
      return `${assetBase}${encodeURIComponent(part.fileName)}`;
    }

    function keepsPreviewMeshOpaque(part) {
      const label = `${part?.id || ""} ${part?.name || ""} ${part?.fileName || ""}`.toLowerCase();
      return label.includes("tailribs") || label.includes("tail ribs") || label.includes("slider");
    }

    function buildSwatches() {
      const palette = model.colorPalettes.find((item) => item.id === model.colorPaletteId) || model.colorPalettes[0];
      swatches.innerHTML = "";
      palette.colorPaletteColorCodes.forEach((hex, index) => {
        const normalized = cleanHex(hex);
        const button = document.createElement("button");
        button.type = "button";
        button.style.setProperty("--swatch", normalized);
        button.title = colorName(normalized);
        button.setAttribute("aria-label", colorName(normalized));
        button.classList.toggle("is-active", index === 0);
        button.addEventListener("click", () => {
          state.selectedColor = normalized;
          [...swatches.children].forEach((item) => item.classList.toggle("is-active", item === button));
          updateCurrentColor();
        });
        swatches.append(button);
      });
      state.selectedColor = cleanHex(palette.colorPaletteColorCodes[0]);
      updateCurrentColor();
    }

    function updateCurrentColor() {
      if (currentColor) currentColor.textContent = tr("selected_color", { color: colorName(state.selectedColor) });
    }

    function buttonMode(button, mode) {
      button.addEventListener("click", () => {
        state.selectedMode = mode;
        [...modes.children].forEach((item) => item.classList.toggle("is-active", item === button));
        setStatus(tr("color_mode", { mode: button.textContent }));
      });
    }

    function displayModeName(name) {
      return String(name || "");
    }

    function buildModes() {
      modes.innerHTML = "";
      const individual = document.createElement("button");
      individual.type = "button";
      individual.textContent = tr("individual_parts");
      individual.classList.toggle("is-active", !defaultModeSet);
      buttonMode(individual, { type: "individual" });
      modes.append(individual);

      model.colorPartGroups.forEach((group) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = runtimeLabel("part_groups", group.id, displayModeName(group.name));
        buttonMode(button, { type: "group", groupId: group.id });
        modes.append(button);
      });

      model.colorPartGroupSets.forEach((set) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = runtimeLabel("group_sets", set.id, displayModeName(set.name));
        button.classList.toggle("is-active", defaultModeSet?.id === set.id);
        buttonMode(button, { type: "set", setId: set.id });
        modes.append(button);
      });
    }

    function targetPartIds(clickedPartId) {
      if (state.selectedMode.type === "individual") return [clickedPartId];
      if (state.selectedMode.type === "group") {
        const group = model.colorPartGroups.find((item) => item.id === state.selectedMode.groupId);
        return group?.partIds || [clickedPartId];
      }
      if (state.selectedMode.type === "set") {
        const set = model.colorPartGroupSets.find((item) => item.id === state.selectedMode.setId);
        const group = set?.groups.find((item) => item.partIds.includes(clickedPartId));
        return group?.partIds || [clickedPartId];
      }
      return [clickedPartId];
    }

    function applyColor(clickedPartId) {
      targetPartIds(clickedPartId).forEach((partId) => {
        const mesh = state.meshes.get(partId);
        const part = partsById.get(partId);
        if (!mesh || part?.hasFixedColor) return;
        mesh.material.color.set(state.selectedColor);
        state.partColors.set(partId, state.selectedColor);
        state.coloredPartIds.add(partId);
      });
      clearShareLink();
      setStatus(tr("applied_color", { color: colorName(state.selectedColor) }));
    }

    function preferredViewZoom() {
      const width = canvas.parentElement?.getBoundingClientRect().width || 0;
      return width > 0 && width < COMPACT_VIEW_MAX_WIDTH ? COMPACT_VIEW_ZOOM : DEFAULT_VIEW_ZOOM;
    }

    function syncResponsiveZoom() {
      const nextDefaultZoom = preferredViewZoom();
      if (state.zoom === state.defaultZoom) {
        state.zoom = nextDefaultZoom;
      }
      state.defaultZoom = nextDefaultZoom;
    }

    function resizeRenderer() {
      if (!renderer || !camera) return;
      const box = canvas.parentElement.getBoundingClientRect();
      const width = Math.max(320, Math.floor(box.width));
      const height = Math.max(320, Math.floor(box.height));
      renderer.setSize(width, height, false);
      syncResponsiveZoom();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }

    function updateModelTransform() {
      if (!rootGroup) return;
      rootGroup.rotation.y = state.yaw;
      rootGroup.rotation.x = state.pitch;
      state.meshes.forEach((mesh, partId) => {
        const part = partsById.get(partId);
        mesh.position.y = state.expanded ? part.expandLevel * 3.8 : 0;
      });
      if (camera) camera.position.z = state.zoom;
    }

    function renderLoop() {
      if (!renderer || !scene || !camera) return;
      updateModelTransform();
      renderer.render(scene, camera);
      window.requestAnimationFrame(renderLoop);
    }

    function parseLegacyGeometry(json) {
      const geometry = new THREE.Geometry();
      const vertices = json.vertices || [];
      const faces = json.faces || [];
      for (let index = 0; index < vertices.length; index += 3) {
        geometry.vertices.push(new THREE.Vector3(vertices[index], vertices[index + 1], vertices[index + 2]));
      }

      let offset = 0;
      while (offset < faces.length) {
        const type = faces[offset++];
        const isQuad = (type & 1) === 1;
        const hasMaterial = (type & 2) === 2;
        const hasFaceUv = (type & 4) === 4;
        const hasFaceVertexUv = (type & 8) === 8;
        const hasFaceNormal = (type & 16) === 16;
        const hasFaceVertexNormal = (type & 32) === 32;
        const hasFaceColor = (type & 64) === 64;
        const hasFaceVertexColor = (type & 128) === 128;
        const vertexCount = isQuad ? 4 : 3;
        const faceVertices = faces.slice(offset, offset + vertexCount);
        offset += vertexCount;
        if (hasMaterial) offset += 1;
        if (hasFaceUv) offset += 1;
        if (hasFaceVertexUv) offset += vertexCount;
        if (hasFaceNormal) offset += 1;
        if (hasFaceVertexNormal) offset += vertexCount;
        if (hasFaceColor) offset += 1;
        if (hasFaceVertexColor) offset += vertexCount;

        if (isQuad) {
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[1], faceVertices[2]));
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[2], faceVertices[3]));
        } else {
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[1], faceVertices[2]));
        }
      }
      geometry.computeFaceNormals();
      return geometry;
    }

    async function loadPart(part) {
      const response = await fetch(meshUrl(part), { credentials: "same-origin" });
      if (!response.ok) throw new Error(tr("mesh_load_failed", { file: part.fileName }));
      const json = await response.json();
      const geometry = parseLegacyGeometry(json);
      geometry.computeBoundingBox();
      geometry.computeVertexNormals();
      const shouldStayOpaque = keepsPreviewMeshOpaque(part);
      const material = new THREE.MeshPhongMaterial({
        color: state.partColors.get(part.id) || DEFAULT_COLOR,
        shininess: 18,
        side: THREE.DoubleSide,
        transparent: !shouldStayOpaque,
        opacity: shouldStayOpaque ? 1 : PREVIEW_MESH_OPACITY
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.partId = part.id;
      mesh.userData.partName = part.name;
      state.meshes.set(part.id, mesh);
      rootGroup.add(mesh);
      return mesh;
    }

    function centerModel() {
      const box = new THREE.Box3().setFromObject(rootGroup);
      const center = new THREE.Vector3();
      box.getCenter(center);
      rootGroup.children.forEach((mesh) => {
        mesh.position.x -= center.x;
        mesh.position.y -= center.y;
        mesh.position.z -= center.z;
      });
    }

    async function initThree() {
      if (!window.THREE) {
        throw new Error(tr("three_not_loaded"));
      }
      if (!THREE.Geometry || !THREE.Face3) {
        throw new Error(tr("three_legacy_required"));
      }

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(38, 1, 0.1, 1000);
      camera.position.set(0, 0, state.zoom);
      rootGroup = new THREE.Group();
      scene.add(rootGroup);
      raycaster = new THREE.Raycaster();
      pointerVector = new THREE.Vector2();

      [
        [0, 15, 20, 0.9],
        [20, 12, 10, 0.55],
        [-18, -10, 15, 0.45],
        [0, -20, -20, 0.35]
      ].forEach(([x, y, z, intensity]) => {
        const light = new THREE.DirectionalLight(0xffffff, intensity);
        light.position.set(x, y, z);
        scene.add(light);
      });
      scene.add(new THREE.AmbientLight(0xffffff, 0.35));

      let loaded = 0;
      for (const part of model.parts) {
        if (loading) loading.textContent = tr("loading_part", { current: loaded + 1, total: model.parts.length });
        await loadPart(part);
        loaded += 1;
      }
      centerModel();
      resizeRenderer();
      state.ready = true;
      if (loading) loading.hidden = true;
      if (state.loadedFromShare) setStatus(tr("shared_design_loaded"));
      renderLoop();
    }

    function pickPart(event) {
      if (!state.ready) return;
      const rect = canvas.getBoundingClientRect();
      pointerVector.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointerVector.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerVector, camera);
      const intersections = raycaster.intersectObjects([...state.meshes.values()], false);
      if (intersections[0]) {
        applyColor(intersections[0].object.userData.partId);
      }
    }

    function bindPointer() {
      canvas.addEventListener("pointerdown", (event) => {
        canvas.setPointerCapture(event.pointerId);
        state.pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          yaw: state.yaw,
          pitch: state.pitch,
          moved: 0
        };
      });
      canvas.addEventListener("pointermove", (event) => {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;
        const dx = event.clientX - state.pointer.x;
        const dy = event.clientY - state.pointer.y;
        state.pointer.moved = Math.max(state.pointer.moved, Math.abs(dx) + Math.abs(dy));
        state.yaw = state.pointer.yaw + dx * 0.01;
        state.pitch = Math.max(-1.1, Math.min(0.9, state.pointer.pitch + dy * 0.008));
      });
      canvas.addEventListener("pointerup", (event) => {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;
        const moved = state.pointer.moved;
        state.pointer = null;
        if (moved < 8) pickPart(event);
      });
      canvas.addEventListener("wheel", (event) => {
        event.preventDefault();
        state.zoom = Math.max(MIN_VIEW_ZOOM, Math.min(MAX_VIEW_ZOOM, state.zoom + Math.sign(event.deltaY) * 2));
      }, { passive: false });
    }

    function snapshot() {
      if (!renderer || !scene || !camera) return "";
      const priorYaw = state.yaw;
      const priorPitch = state.pitch;
      const priorZoom = state.zoom;
      state.yaw = -0.8;
      state.pitch = -0.1;
      state.zoom = 24;
      updateModelTransform();
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL("image/png");
      state.yaw = priorYaw;
      state.pitch = priorPitch;
      state.zoom = priorZoom;
      if (snapshotInput) snapshotInput.value = dataUrl;
      return dataUrl;
    }

    function uncoloredParts() {
      return model.parts.filter((part) => !part.hasFixedColor && !state.coloredPartIds.has(part.id));
    }

    function missingPartSummary(parts) {
      const groups = model.colorPartGroups
        .filter((group) => Number(group.id) !== 1901)
        .map((group) => {
          const missingIds = parts.filter((part) => group.partIds.includes(part.id)).map((part) => part.id);
          return missingIds.length ? { name: runtimeLabel("part_groups", group.id, group.name), count: missingIds.length, ids: missingIds } : null;
        })
        .filter(Boolean);
      const coveredIds = new Set(groups.flatMap((group) => group.ids));
      const ungrouped = parts.filter((part) => !coveredIds.has(part.id));
      return [
        ...groups.map((group) => `${group.name} (${group.count})`),
        ...ungrouped.slice(0, 6).map((part) => runtimeLabel("parts", part.id, part.name))
      ].join(", ");
    }

    function assertCompleteDesign(actionLabel) {
      const missing = uncoloredParts();
      if (!missing.length) return;
      const noun = missing.length === 1 ? tr("parts_need_color_one") : tr("parts_need_color_other");
      const summary = missingPartSummary(missing);
      const message = tr("complete_design", { action: actionLabel, count: missing.length, noun, summary: summary ? `: ${summary}` : "", instruction: tr("complete_design_instruction") });
      setStatus(message, "error");
      status?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      const error = new Error(message);
      error.name = "ValidationError";
      throw error;
    }

    function designPayload(options = {}) {
      const includeSnapshot = options.includeSnapshot === true;
      const partColors = model.parts.map((part) => ({
        partId: part.id,
        partName: part.name,
        color: state.partColors.get(part.id) || DEFAULT_COLOR
      }));
      return {
        id: `pd-valkyrie-${Date.now()}`,
        modelId: model.canopyModelId,
        modelName: model.name,
        sizeId: sizeSelect.value,
        size: sizeSelect.options[sizeSelect.selectedIndex]?.textContent || "",
        name: state.designName,
        savedAt: new Date().toISOString(),
        partColors,
        snapshot: includeSnapshot ? snapshot() : ""
      };
    }

    function partColorString(payload) {
      return payload.partColors.map((item) => `${item.partId}:${compactHex(item.color)}`).join(";");
    }

    function applyPartColorString(value) {
      String(value || "").split(";").forEach((pair) => {
        const [id, color] = pair.split(":");
        const partId = Number(id);
        if (!partsById.has(partId) || !/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color || "")) return;
        const normalized = cleanHex(color);
        state.partColors.set(partId, normalized);
        state.coloredPartIds.add(partId);
        const mesh = state.meshes.get(partId);
        if (mesh) mesh.material.color.set(normalized);
      });
    }

    function compactShareState(payload) {
      return {
        v: 1,
        m: payload.modelId,
        s: payload.sizeId,
        n: payload.name,
        c: partColorString(payload)
      };
    }

    function fallbackShareUrl(payload) {
      const url = new URL(window.location.href);
      url.searchParams.set(SHARE_PARAM, encodeBase64Url(JSON.stringify(compactShareState(payload))));
      url.hash = "designer";
      return url.toString();
    }

    function loadSharedDesignFromUrl() {
      const token = new URL(window.location.href).searchParams.get(SHARE_PARAM);
      if (!token) return false;
      try {
        const shared = JSON.parse(decodeBase64Url(token));
        if (shared.v !== 1 || Number(shared.m) !== Number(model.canopyModelId) || !shared.c) {
          throw new Error(tr("design_wrong_model"));
        }
        if (shared.n) state.designName = shared.n;
        if (shared.s && [...sizeSelect.options].some((option) => option.value === String(shared.s))) {
          sizeSelect.value = String(shared.s);
        }
        applyPartColorString(shared.c);
        state.loadedFromShare = true;
        setShareLink(window.location.href);
        return true;
      } catch (error) {
        console.error(error);
        setStatus(tr("design_link_invalid"), "error");
        return false;
      }
    }

    function fallbackHash(value) {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return Math.abs(hash).toString(16).padStart(8, "0").repeat(3).slice(0, 24);
    }

    async function sha256(value) {
      if (!window.crypto?.subtle) return fallbackHash(value);
      const bytes = new TextEncoder().encode(value);
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    async function localPersistedDesign(payload) {
      const compactColors = partColorString(payload);
      const designHash = (await sha256(`${payload.modelId}|${payload.sizeId}|${payload.name}|${compactColors}`)).slice(0, 24);
      return {
        design_ref: `pd-valkyrie-${designHash.slice(0, 12)}`,
        design_hash: designHash,
        preview_url: "",
        local_only: true
      };
    }

    async function persistDesign(payload, shareUrl) {
      const compactColors = partColorString(payload);
      if (!designSaveUrl) {
        return localPersistedDesign(payload);
      }
      const response = await fetch(designSaveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          model_id: payload.modelId,
          model: payload.modelName,
          size_id: payload.sizeId,
          size: payload.size,
          design_name: payload.name,
          part_colors: compactColors,
          snapshot: payload.snapshot,
          fallback_share_url: shareUrl
        })
      });
      if (!response.ok) throw new Error(tr("design_save_failed"));
      const result = await response.json();
      const local = await localPersistedDesign(payload);
      return {
        ...result,
        design_ref: result.design_ref || local.design_ref,
        design_hash: result.design_hash || local.design_hash,
        preview_url: result.preview_url || "",
        share_url: result.share_url || result.shareUrl || result.design_url || result.url || ""
      };
    }

    async function createShareLink(options = {}) {
      assertCompleteDesign(tr("save_design"));
      const payload = designPayload({ includeSnapshot: Boolean(designSaveUrl) });
      const fallbackUrl = fallbackShareUrl(payload);
      if (state.currentShare?.signature === fallbackUrl) return state.currentShare;

      let persisted;
      let shareUrl = fallbackUrl;
      let backendUnavailable = false;
      try {
        persisted = await persistDesign(payload, fallbackUrl);
        const persistedUrl = persisted.share_url || persisted.shareUrl || persisted.design_url || persisted.url || fallbackUrl;
        shareUrl = await shortenShareUrl(persistedUrl);
      } catch (error) {
        console.error(error);
        backendUnavailable = true;
        persisted = await localPersistedDesign(payload);
      }

      state.currentShare = {
        signature: fallbackUrl,
        url: shareUrl,
        fallbackUrl,
        persisted,
        payload
      };
      setShareLink(shareUrl);
      return state.currentShare;
    }

    function fallbackCopy(value) {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-999px";
      document.body.append(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }

    async function copyShareLink() {
      const share = state.currentShare || await createShareLink({ announce: false });
      try {
        if (!navigator.clipboard?.writeText) throw new Error(tr("copy_failed"));
        await navigator.clipboard.writeText(share.url);
      } catch {
        fallbackCopy(share.url);
      }
      setStatus(tr("design_link_copied"));
    }

    async function shareToWhatsApp() {
      const share = state.currentShare || await createShareLink({ announce: false });
      window.open(whatsappShareUrl(share.url), "_blank", "noopener");
      setStatus(tr("opening_whatsapp"));
    }

    async function addCustomToCart() {
      const share = await createShareLink({ announce: false });
      const { payload, persisted } = share;
      const sliderChoice = selectedSliderChoice();
      const sliderVariant = selectedSliderVariant();
      const canopyVariant = selectedCanopyVariant();
      const customVariantId = canopyVariant?.id || variantId;
      if (!customVariantId || customVariantId === "undefined") {
        throw new Error(tr("no_custom_variant", { size: payload.size }));
      }
      if (!enableCartPosts) {
        return { previewOnly: true };
      }
      if (sliderChoice.variantStyle && !sliderVariant) {
        throw new Error(tr("custom_slider_missing", { slider: sliderChoice.label, size: RDS_SLIDER_DEFAULT_SIZE }));
      }

      const canopyProperties = {
        "Order type": "Custom PD design",
        Model: payload.modelName,
        Size: payload.size,
        Slider: sliderChoice.cartLabel,
        "Design link": share.url,
        "_pd_design_ref": persisted.design_ref,
        "_pd_design_hash": persisted.design_hash,
        "_pd_model_id": String(payload.modelId),
        "_pd_size_id": String(payload.sizeId),
        "_pd_slider_choice": sliderSelect?.value || "standard"
      };
      const items = [
        {
          id: Number(customVariantId),
          quantity: 1,
          properties: canopyProperties
        }
      ];

      if (sliderVariant) {
        items.push({
          id: Number(sliderVariant.id),
          quantity: 1,
          properties: {
            "Order type": "PD RDS slider add-on",
            "For canopy": "Custom PD Valkyrie",
            "Selected Valkyrie size": payload.size,
            "Requested slider": sliderChoice.cartLabel,
            "Default slider size": RDS_SLIDER_DEFAULT_SIZE,
            "Slider size note": "Default 21 x 30 selected online; customer/adviser to confirm exact PD slider size before fulfilment.",
            "Linked design": share.url,
            "_pd_design_ref": persisted.design_ref,
            "_pd_design_hash": persisted.design_hash,
            "_pd_parent_model": payload.modelName,
            "_pd_parent_size_id": String(payload.sizeId),
            "_pd_slider_default_size": RDS_SLIDER_DEFAULT_SIZE
          }
        });
      }

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error(tr("custom_cart_rejected"));
      const result = await response.json();
      refreshCartDrawer();
      return result;
    }

    designer.querySelector("[data-designer-reset]")?.addEventListener("click", () => {
      state.partColors = new Map(model.parts.map((part) => [part.id, part.color || DEFAULT_COLOR]));
      state.coloredPartIds = new Set(model.parts.filter((part) => !part.hasFixedColor && part.color).map((part) => part.id));
      state.meshes.forEach((mesh, partId) => mesh.material.color.set(state.partColors.get(partId)));
      clearShareLink();
      setStatus(tr("colors_reset"));
    });

    shareButton?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = tr("saving");
      try {
        await createShareLink();
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error?.name === "ValidationError" ? error.message : tr("check_settings"), "error");
      } finally {
        button.disabled = false;
        button.textContent = tr("save_design");
      }
    });

    copyLinkButton?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      try {
        await copyShareLink();
        button.textContent = tr("copied");
        button.classList.add("pd-share-action--success");
        window.setTimeout(() => {
          button.textContent = tr("copy_design_link");
          button.classList.remove("pd-share-action--success");
        }, 2000);
      } catch (error) {
        console.error(error);
        setStatus(tr("copy_failed"), "error");
      }
    });

    whatsAppButton?.addEventListener("click", async (event) => {
      event.preventDefault();
      try {
        await shareToWhatsApp();
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error?.name === "ValidationError" ? error.message : tr("check_settings"), "error");
      }
    });

    designer.querySelector("[data-designer-add]")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = tr("adding");
      try {
        const result = await addCustomToCart();
        const sliderChoice = selectedSliderChoice();
        button.textContent = result.previewOnly ? tr("cart_preview_ready") : tr("added_to_cart");
        setStatus(result.previewOnly ? tr("designer_status_preview") : tr("designer_status_added"), "success");
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error?.name === "ValidationError" ? error.message : tr("check_settings"), "error");
        button.textContent = tr("check_settings");
      } finally {
        setTimeout(() => {
          button.disabled = false;
          button.textContent = tr("add_custom_design");
        }, 2600);
      }
    });

    designer.querySelectorAll("[data-view]").forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.view;
        if (action === "zoom-in") state.zoom = Math.max(MIN_VIEW_ZOOM, state.zoom - 3);
        if (action === "zoom-out") state.zoom = Math.min(MAX_VIEW_ZOOM, state.zoom + 3);
        if (action === "expand") {
          state.expanded = !state.expanded;
          button.textContent = state.expanded ? "⤡" : "⤢";
          button.title = state.expanded ? tr("contract_canopy") : tr("expand_canopy");
          button.setAttribute("aria-label", state.expanded ? tr("contract_canopy") : tr("expand_canopy"));
        }
        if (action === "reset-view") {
          state.yaw = -0.8;
          state.pitch = -0.1;
          state.zoom = preferredViewZoom();
          state.defaultZoom = state.zoom;
        }
      });
    });

    window.addEventListener("resize", resizeRenderer);
    sizeSelect?.addEventListener("change", clearShareLink);
    buildSwatches();
    buildModes();
    bindPointer();
    loadSharedDesignFromUrl();
    initThree().catch((error) => {
      console.error(error);
      if (loading) loading.textContent = tr("designer_load_error");
      setStatus(tr("designer_load_error"), "error");
    });
  }

  function scrollToActiveHash() {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    window.requestAnimationFrame(() => target.scrollIntoView({ block: "start" }));
  }

  async function initSection(section) {
    configureTranslations(section);
    initTabs(section);
    initHeroGallery(section);
    try {
      const liveStockUrl = liveStockRequestUrl(section.dataset.liveStockUrl, section);
      const staticStockUrl = section.dataset.stockUrl;
      const mergeStockData = (liveData, staticData) => {
        const scopedLiveData = stockDataForSection(section, liveData);
        const scopedStaticData = stockDataForSection(section, staticData);
        const liveItems = Array.isArray(scopedLiveData?.items) ? scopedLiveData.items : [];
        const staticItems = Array.isArray(scopedStaticData?.items) ? scopedStaticData.items : [];
        if (!liveItems.length) return staticItems.length ? scopedStaticData : scopedLiveData;
        const merged = [...liveItems];
        const seen = new Set(liveItems.map((item) => String(item.stock_id)));
        staticItems.forEach((item) => {
          if (/\b(used|resale)\b/i.test(String(item.category || item.details?.category || item.details?.pattern || "")) && !seen.has(String(item.stock_id))) {
            merged.push(item);
          }
        });
        return { ...(scopedLiveData || scopedStaticData || {}), items: merged, count: merged.length };
      };
      const stockPromise = liveStockUrl
        ? Promise.allSettled([getJson(liveStockUrl), getJson(staticStockUrl)])
            .then(([liveResult, staticResult]) => {
              const liveData = liveResult.status === "fulfilled" ? liveResult.value : null;
              const staticData = staticResult.status === "fulfilled" ? staticResult.value : null;
              return mergeStockData(liveData, staticData);
            })
        : getJson(staticStockUrl);
      const [stockData, templateData, colorData] = await Promise.all([
        stockPromise,
        getJson(section.dataset.templateUrl),
        getJson(section.dataset.colorsUrl)
      ]);
      initStock(section, stockData);
      initDesigner(section, templateData, colorData);
      scrollToActiveHash();
    } catch (error) {
      console.error(error);
      const grid = section.querySelector(SELECTORS.stockGrid);
      if (grid) grid.innerHTML = `<p class="pd-error">${escapeHTML(tr("check_settings"))}</p>`;
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
  });
})();
